import { useCallback, useEffect, useMemo, useState } from 'react';
import { RegexValidators } from '@libs/validations';
import { formatDate, isDateBefore, getCurrentDate, getDateFromString, getNativeDateObject, } from '@libs/hooks';
const DATE_FILTER_FORMAT = 'DD.MM.YY';
const TODAY = getNativeDateObject(getCurrentDate());
export const useDateFilter = (isExactDayFilter, defaultValue) => {
    const [isExactDaySelected, setIsExactDaySelected] = useState(isExactDayFilter);
    const [selectedDates, setSelectedDates] = useState(defaultValue);
    useEffect(() => {
        if (defaultValue) {
            setSelectedDates(defaultValue);
        }
    }, [defaultValue]);
    const [isOpen, setIsOpen] = useState(false);
    const [{ from, to }, setDateFormats] = useState({
        from: '',
        to: '',
    });
    const toggleSelect = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);
    const handleCalendarWrapperClick = (e) => {
        e.stopPropagation();
    };
    const selectDate = (inputName, value) => {
        const { date: newDate, isValid } = getDateFromString(value, '.');
        if (!isValid || !newDate)
            return;
        if (!isDateBefore(newDate, TODAY)) {
            // cannot select in the future
            setDateFormats((prev) => ({
                ...prev,
                [inputName]: '',
            }));
            return;
        }
        setSelectedDates((prev) => {
            if (Array.isArray(prev) && !isExactDaySelected) {
                if (inputName === 'from') {
                    return [newDate, prev?.[1] || null];
                }
                else {
                    return [prev?.[0] || null, newDate];
                }
            }
            return isExactDaySelected ? newDate : [newDate, null];
        });
    };
    const onBlur = (e) => {
        const { value, name } = e.target;
        selectDate(name, value);
    };
    const handleEmptyDate = (name, value) => {
        if (value.length === 0 && Array.isArray(selectedDates)) {
            if (name === 'from') {
                setSelectedDates(selectedDates?.[1] ? [null, selectedDates[1]] : undefined);
            }
            else {
                setSelectedDates(selectedDates?.[0] ? [selectedDates[0], null] : undefined);
            }
        }
    };
    const updateDateFormats = (name, value) => {
        setDateFormats((prev) => ({
            ...prev,
            [name]: value,
        }));
    };
    const handleInputChange = (e) => {
        const { value, name } = e.target;
        const sanitizedValue = value.replace(RegexValidators.ONLY_NUMERIC_CHARS_AND_PERIODS, '');
        if (!RegexValidators.DOTTED_PAIR_NUMBER_FORMAT.test(sanitizedValue)) {
            return;
        }
        updateDateFormats(name, sanitizedValue);
        handleEmptyDate(name, sanitizedValue);
        if (sanitizedValue.length === 8) {
            selectDate(name, sanitizedValue);
        }
        if (sanitizedValue.length === 0 && isExactDaySelected) {
            setSelectedDates(undefined);
        }
    };
    useEffect(() => {
        const arrayLength = (Array.isArray(selectedDates) && selectedDates.length) || 0;
        if (!selectedDates && arrayLength === 0) {
            setDateFormats({
                from: '',
                to: '',
            });
            return;
        }
        if (!Array.isArray(selectedDates)) {
            setDateFormats({
                from: formatDate(DATE_FILTER_FORMAT, selectedDates) || '',
                to: '',
            });
            return;
        }
        if (Array.isArray(selectedDates) && !selectedDates[1]) {
            setDateFormats({
                from: formatDate(DATE_FILTER_FORMAT, selectedDates[0]) || '',
                to: '',
            });
            return;
        }
        const [fromDate, toDate] = selectedDates;
        setDateFormats({
            from: formatDate(DATE_FILTER_FORMAT, fromDate) || '',
            to: formatDate(DATE_FILTER_FORMAT, toDate) || '',
        });
    }, [selectedDates]);
    useEffect(() => {
        if (isExactDaySelected && Array.isArray(selectedDates)) {
            const [from] = selectedDates;
            setSelectedDates(from);
        }
    }, [selectedDates, isExactDaySelected]);
    const selectedDatesLabel = useMemo(() => {
        if (isExactDaySelected && from) {
            return from;
        }
        if (!isExactDaySelected && from && to) {
            return from + '-' + to;
        }
        if (from) {
            return from;
        }
        return '';
    }, [isExactDaySelected, from, to]);
    const closeFilter = useCallback(() => {
        setIsOpen(false);
    }, []);
    return {
        to,
        from,
        isOpen,
        selectedDates,
        maxDate: TODAY,
        selectedDatesLabel,
        isExactDaySelected,
        handleCalendarWrapperClick,
        setIsExactDaySelected,
        handleInputChange,
        setSelectedDates,
        toggleSelect,
        closeFilter,
        onBlur,
    };
};
//# sourceMappingURL=useDateFilter.js.map